import { Failure, Ok } from "@megaron/result";

import { Serializer } from "./serializer";

type SerializersWithExtensions<T> = Serializer<T> & SerializerExtensions<T>;

export type JsonStringSerializer<T> = Omit<SerializersWithExtensions<T>, "serialize"> & {
  serialize: (input: T) => string;
};

const nullableExtension = <T>(
  serializer: Serializer<T>,
  opts?: { emptyStrAsNull?: boolean },
): SerializersWithExtensions<T | null> => ({
  ...serializer,
  serialize: (value) => {
    if (value === null) return value;
    return serializer.serialize(value);
  },
  deserialize: (raw) => {
    if (raw === "" && opts?.emptyStrAsNull) return Ok(null);
    if (raw === null) return Ok(raw);
    return serializer.deserialize(raw);
  },
  ...serializerExtensions(),
});

export const optionalExtension = <T>(
  serializer: Serializer<T>,
  opts?: { emptyStrAsUndefined?: boolean },
): SerializersWithExtensions<T | undefined> => ({
  ...serializer,
  serialize: (value) => {
    if (value === undefined) return value;
    return serializer.serialize(value);
  },
  deserialize: (raw) => {
    if (raw === "" && opts?.emptyStrAsUndefined) return Ok(undefined);
    if (raw === undefined) return Ok(raw);
    return serializer.deserialize(raw);
  },
  ...serializerExtensions(),
});

export const jsonStringExtension = <T>(serializer: Serializer<T>): JsonStringSerializer<T> => {
  const wrapped = jsonStringSerializer(serializer);

  return {
    ...serializer,
    serialize: wrapped.serialize,
    deserialize: wrapped.deserialize,
    ...serializerExtensions(),
  };
};

export const arrayExtension = <T>(serializer: Serializer<T>): SerializersWithExtensions<T[]> => {
  const wrapped = arraySerializer(serializer);

  return {
    ...serializer,
    serialize: wrapped.serialize,
    deserialize: wrapped.deserialize,
    ...serializerExtensions(),
  };
};

export interface SerializerExtensions<T> {
  nullable: (opts?: { emptyStrAsNull?: boolean }) => SerializersWithExtensions<T | null>;
  optional: (opts?: { emptyStrAsUndefined?: boolean }) => SerializersWithExtensions<T | undefined>;
  asJsonString: () => JsonStringSerializer<T>;
  array: () => SerializersWithExtensions<T[]>;
}

export const serializerExtensions = <T>(): SerializerExtensions<T> => ({
  nullable(this: any, opts?: { emptyStrAsNull?: boolean }) {
    return nullableExtension(this, opts);
  },
  optional(this: any, opts?: { emptyStrAsUndefined?: boolean }) {
    return optionalExtension(this, opts);
  },
  asJsonString(this: any) {
    return jsonStringExtension(this);
  },
  array(this: any) {
    return arrayExtension(this);
  },
});

const arraySerializer = <T>(elementSerializer: Serializer<T>): Serializer<T[]> & SerializerExtensions<T[]> => ({
  serialize: (items) => items.map(elementSerializer.serialize),
  deserialize: (items) => {
    if (!Array.isArray(items)) return Failure("NotAnArray");

    const result: T[] = [];
    const errors: { [index: number]: unknown } = {};
    let hasErrors = false;

    for (const [index, item] of items.entries()) {
      const deserialized = elementSerializer.deserialize(item);
      if (deserialized.isOk) {
        result.push(deserialized.value);
      } else {
        hasErrors = true;
        errors[index] = deserialized.error;
      }
    }

    if (hasErrors) return Failure(errors);
    return Ok(result);
  },
  ...serializerExtensions(),
});

const jsonStringSerializer = <T>(contentSerializer: Serializer<T>): JsonStringSerializer<T> => ({
  serialize: (value) => JSON.stringify(contentSerializer.serialize(value)),
  deserialize: (raw) => {
    if (typeof raw !== "string") return Failure("NotAString");
    try {
      const parsed = JSON.parse(raw);
      return contentSerializer.deserialize(parsed);
    } catch (e) {
      return Failure("InvalidJson");
    }
  },
  ...serializerExtensions(),
});
